/**
 * Human URLs (DEV13-40): identifier-keyed with a decorative title slug.
 *
 * The backend resolves `/projects/dev13/` by project identifier and
 * `/projects/dev13/work-items/DEV13-51-any-title/` by identifier + number —
 * the title suffix is decorative and ignored, so renames never break shared
 * links. UUID public_ids keep resolving too; these helpers fall back to them
 * whenever an identifier isn't in the payload.
 */

/** Lowercase, ASCII-ish, hyphen-joined, capped at a word boundary — safe as a
 * URL decoration (no mid-word cuts like `…-maybe-wra`). */
export function slugifyTitle(title: string): string {
  const slug = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (slug.length <= 60) return slug;
  const cut = slug.slice(0, 60);
  const lastBreak = cut.lastIndexOf('-');
  return (lastBreak > 0 ? cut.slice(0, lastBreak) : cut).replace(/-+$/, '');
}

export function projectPath(project: { identifier?: string | null; public_id: string }): string {
  return `/projects/${(project.identifier || project.public_id).toLowerCase()}`;
}

export function workItemPath(
  project: { identifier?: string | null; public_id: string },
  item: { identifier?: string | null; public_id: string; title?: string | null },
): string {
  const base = projectPath(project);
  if (!item.identifier) return `${base}/work-items/${item.public_id}`;
  const slug = item.title ? slugifyTitle(item.title) : '';
  return `${base}/work-items/${item.identifier.toLowerCase()}${slug ? `-${slug}` : ''}`;
}

/** Compact ref for selection state (`?item=globex-2`) — readable in the URL
 * bar; falls back to the public_id when the payload has no identifier. */
export function workItemRef(item: { identifier?: string | null; public_id: string }): string {
  return (item.identifier || item.public_id).toLowerCase();
}
